import { Injectable } from '@angular/core';
import { ApiService } from '../../shared/services/core/api.service';
import { Observable, of } from 'rxjs';
import { CompanyOfferModel } from './models/company-offer.model';
import { CompanyOfferV2Model } from './models/company-offer-v2.model';
import { CompanyOfferStore } from './company-offer.store';
import { mapTo, tap } from 'rxjs/operators';
import { CompanyOfferProcessorResult } from './models/company-offer-processor-result.model';
import { LoyaltyCategoryType } from '../../shared/models/loyalty-category-types.model';
import { ClubCategoryTag } from '../../shared/models/club-category-tag.model';

@Injectable({ providedIn: 'root' })
export class CompanyOfferService {
  private offersCategories: ClubCategoryTag[] = [];

  constructor(
    private companyOfferStore: CompanyOfferStore,
    private api: ApiService
  ) {}

  /**
   * Fetch company offers for a given company and department
   * If CompanyId is set to 0, all Company offers are returned
   * If DepartmentId is set to 0 and Company set to 1, all Departments for CompanyId = 1 will be returned
   * If CompanyId and DepartmentId are 0, all offers for all comapnies/departments are retreived
   * @param companyId - company to fetch offers for - if 0 it will fetch all companies
   * @param departmentId - which company department to retreive offers for - if 0 all departments returned
   */
  fetchOffers(companyId: Number, departmentId: number) {
    const request$: Observable<null> = this.api
      .getAnySingle<CompanyOfferModel[]>(`companies/offers?companyId=${companyId}&departmentId=${departmentId}`)
      .pipe(
        tap((offers) => this.companyOfferStore.upsertMany(offers)),
        mapTo(null)
      );
    return request$;
  }

  /**
   * Fetches the offer categories and stores them in the service.
   * If the service already loaded them, returns stored value.
   * @returns array of ClubCategoryTag as observable
   * */
  fetchOfferCategories(): Observable<ClubCategoryTag[]> {
    if (!this.offersCategories.length) {
      return this.api.getAnySingle<ClubCategoryTag[]>(`loyalty/categories/${LoyaltyCategoryType.CompanyOffer}`).pipe(
        tap((categories) => {
          this.offersCategories = categories;
        })
      );
    } else {
      return of(this.offersCategories);
    }
  }

  /**
   * Fetch company offers (v2) for a given company and department
   * If CompanyId is set to 0, all Company offers are returned
   * If DepartmentId is set to 0 and Company set to 1, all Departments for CompanyId = 1 will be returned
   * If CompanyId and DepartmentId are 0, all offers for all companies/departments are retrieved
   * @param companyId - company to fetch offers for - if 0 it will fetch all companies
   * @param departmentId - which company department to retrieve offers for - if 0 all departments returned
   */
  fetchOffersV2(companyId: number, departmentId: number): Observable<CompanyOfferV2Model[]> {
    return this.api.getAnySingle<CompanyOfferV2Model[]>(`companies/v2/offers`);
  }

  //TODO: This responses for this service method have not yet been fully defined
  async claimOffer(offerId: number): Promise<CompanyOfferProcessorResult> {
    return await this.api.post(`companies/claim/${offerId}`, offerId).toPromise();
  }
}
